import moment from "moment-timezone";

/** Moment format string aligned with existing dashboard screens (Orders, Inventory, etc.). */
export const DEFAULT_DISPLAY_DATE_FORMAT = "MMMM Do YYYY hh:mm:ss A";

/** Compact format for list/table rows so dates stay fully visible without truncation. */
export const LIST_DISPLAY_DATE_FORMAT = "MMM Do YYYY, hh:mm A";

export const DISPLAY_EMPTY = "-";

/**
 * @param input ISO string, epoch ms, Date, or falsy
 * @returns {Moment | null}
 */
export function convertToMomentDateObject(input) {
	if (input == null || input === "") {
		return null;
	}

	if (typeof input === "number") {
		if (!Number.isFinite(input) || input === 0) {
			return null;
		}
		const m = moment(input);
		return m.isValid() ? m : null;
	}

	if (typeof input === "string") {
		const trimmed = input.trim();
		if (!trimmed) {
			return null;
		}
		const momentDateObject = moment(trimmed);
		return momentDateObject.isValid() ? momentDateObject : null;
	}

	if (input instanceof Date) {
		const m = moment(input);
		return m.isValid() ? m : null;
	}

	return null;
}

/**
 * Checks if a time zone is valid.
 * @param {string} timeZone - The time zone to check.
 * @returns {boolean} - True if the time zone is valid, false otherwise.
 */
export function isUsableTimeZone(timeZone) {
	if (!timeZone) {
		return false;
	}
	return Boolean(moment.tz.zone(timeZone));
}

/**
 * Formats a date for UI display in the browser's local timezone.
 * Always returns a string (never undefined).
 * @param {string | number | Date | null} input - The date to format.
 * @param {string} formatString - The format string to use.
 * @returns {string} - The formatted date.
 */
export function formatDisplayDate(input, formatString = DEFAULT_DISPLAY_DATE_FORMAT) {
	const momentDateObject = convertToMomentDateObject(input);
	if (!momentDateObject) {
		return DISPLAY_EMPTY;
	}
	return momentDateObject.format(formatString);
}

export function formatDisplayDateInTZ(input, timeZone, formatString = DEFAULT_DISPLAY_DATE_FORMAT) {
	const momentDateObject = convertToMomentDateObject(input);
	if (!momentDateObject || !isUsableTimeZone(timeZone)) {
		return DISPLAY_EMPTY;
	}
	return momentDateObject.clone().tz(timeZone).format(formatString);
}

export function getTimestampParts(input, { timeZone, formatString = DEFAULT_DISPLAY_DATE_FORMAT }) {
	const momentDateObject = convertToMomentDateObject(input);
	const empty = { restaurant: DISPLAY_EMPTY, local: DISPLAY_EMPTY };
	if (!momentDateObject) {
		return empty;
	}
	const local = momentDateObject.format(formatString);
	if (!isUsableTimeZone(timeZone)) {
		return { restaurant: DISPLAY_EMPTY, local };
	}
	const restaurant = momentDateObject.clone().tz(timeZone).format(formatString);
	return { restaurant, local };
}

export function formatYourTimeTooltip(local) {
	return `Your Time - ${local}`;
}

export function formatDualTimezoneTooltip(restaurant, local) {
	return `Restaurant Time - ${restaurant}\nYour Time - ${local}`;
}
